import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Download, CheckCircle2, XCircle, ChevronDown } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import forge from "node-forge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { publicErrorMessage } from "@/lib/publicErrors";
import { PublicErrorCard } from "@/components/signing/PublicErrorCard";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { generateBudgetPdf } from "@/lib/budgetPdf";
import { resolveProposalPdf } from "@/lib/proposalDocument";
import type { Budget, ServiceType } from "@/hooks/useBudgets";
import logoUrl from "@/assets/brand/simbolo.svg";
import { SignatureTabs, type SignatureTab } from "@/components/signing/SignatureTabs";
import { SignersCard } from "@/components/signing/SignersCard";
import { RejectInline } from "@/components/signing/RejectInline";
import { summarizeInstallments, type PublicInstallment } from "@/lib/installmentSummary";
import { barterSplit, recurringBilling } from "@/lib/barter";
import { formatBRL } from "@/lib/format";
import SEOMeta from "@/components/SEOMeta";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const FN_URL = `${SUPABASE_URL}/functions/v1/sign-budget`;

const fmtCurrency = (v: number) => formatBRL(v);

interface ApiData {
  signature: {
    id: string;
    signer_name: string;
    signer_email: string;
    status: "pending" | "signed" | "rejected";
    expires_at?: string | null;
  };
  budget:
    | (Budget & {
        base_pdf_url?: string;
        base_document_hash?: string;
        service_type?: ServiceType | null;
      })
    | null;
  signers: {
    signer_name: string;
    signer_email: string;
    status: string;
    order_index: number;
    signed_at: string | null;
  }[];
  canSign: boolean;
  payment_plan?: { mode: string; installments: PublicInstallment[] } | null;
}

function SignBudgetView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ApiData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<SignatureTab>("drawn");
  const [typedName, setTypedName] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPwd, setCertPwd] = useState("");
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const sigPadRef = useRef<SignatureCanvas>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${FN_URL}?token=${token}`, {
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Erro");
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const submit = async (payload: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ token, ...payload }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Erro");
      toast.success(
        payload.action === "reject"
          ? "Recusa registrada. Eliveuton será avisado."
          : "Assinatura registrada com sucesso! Você receberá o documento assinado por e-mail.",
      );
      await fetchData();
    } catch (e) {
      toast.error(publicErrorMessage(e instanceof Error ? e.message : null));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = async () => {
    if (tab === "drawn") {
      if (!sigPadRef.current || sigPadRef.current.isEmpty())
        return toast.error("Desenhe sua assinatura");
      const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
      await submit({ signature_type: "drawn", signature_data: dataUrl });
    } else if (tab === "typed") {
      if (typedName.trim().length < 3) return toast.error("Digite seu nome completo");
      if (typedName.trim().toLowerCase() !== data?.signature.signer_name.trim().toLowerCase()) {
        return toast.error("O nome digitado deve corresponder ao nome do signatário");
      }
      await submit({ signature_type: "typed", signature_data: typedName.trim() });
    } else if (tab === "certificate") {
      if (!certFile) return toast.error("Selecione o arquivo .p12/.pfx");
      try {
        const buf = await certFile.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const der = forge.util.createBuffer(binary, "raw");
        const asn1 = forge.asn1.fromDer(der);
        const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, false, certPwd);
        const bags = p12.getBags({ bagType: forge.pki.oids.certBag });
        const certBag = bags[forge.pki.oids.certBag]?.[0];
        if (!certBag?.cert) throw new Error("Certificado não encontrado");
        const cert = certBag.cert;
        const cn = cert.subject.getField("CN")?.value || "";
        const issuer = cert.issuer.getField("CN")?.value || "";
        const validFrom = cert.validity.notBefore.toISOString();
        const validTo = cert.validity.notAfter.toISOString();
        // Sign a payload (token+timestamp) with the private key
        const keyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag });
        const keyBag = keyBags[forge.pki.oids.pkcs8ShroudedKeyBag]?.[0];
        if (!keyBag?.key) throw new Error("Chave privada não encontrada");
        const md = forge.md.sha256.create();
        const payload = `${token}|${Date.now()}`;
        md.update(payload);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const signature = forge.util.encode64((keyBag.key as any).sign(md));
        await submit({
          signature_type: "certificate",
          signature_data: signature,
          certificate_info: {
            cn,
            issuer,
            validFrom,
            validTo,
            payload,
            pem: forge.pki.certificateToPem(cert),
          },
        });
      } catch (e) {
        toast.error("Erro no certificado: " + (e instanceof Error ? e.message : "inválido"));
      }
    }
  };

  const handleReject = async () => {
    if (rejectReason.trim().length < 3) return toast.error("Informe o motivo da rejeição");
    await submit({ action: "reject", reason: rejectReason.trim() });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !data) {
    return <PublicErrorCard code={error} />;
  }

  const { signature: sig, budget, signers, canSign } = data;
  const planRows = summarizeInstallments(
    data.payment_plan?.installments ?? [],
    budget?.net_total ?? 0,
  );
  const done = sig.status !== "pending";
  // One row per package item; a discount row when the package has one.
  const items = budget?.items ?? [];
  const oneTimeItems = items.filter((i) => i.pricing_mode !== "recurring");
  const itemsGross = oneTimeItems.reduce((s, i) => s + i.subtotal, 0);
  const discount = items.length > 0 && budget ? Math.max(itemsGross - budget.gross_total, 0) : 0;
  const fee = budget ? budget.gross_total - budget.net_total : 0;
  const recurringMonthly = budget?.recurring_monthly_total ?? 0;
  const recurringInBarter = budget ? recurringBilling(budget).inBarter : false;
  const barter = barterSplit(budget?.net_total ?? 0, budget?.barter_percent ?? 0);
  const docMode = budget?.document_mode ?? "auto";
  const refCode = `#${sig.id.slice(0, 8).toUpperCase()}`;
  const sentAt = budget?.created_at
    ? format(new Date(budget.created_at), "dd/MM/yyyy", { locale: ptBR })
    : null;
  const validUntil = sig.expires_at
    ? format(new Date(sig.expires_at), "dd/MM/yyyy", { locale: ptBR })
    : null;

  return (
    <div className="min-h-screen bg-background px-6 pb-16 pt-7">
      <div className="mx-auto flex max-w-[780px] flex-col gap-4">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Eliveuton Souza" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-semibold text-foreground">Eliveuton Souza</p>
            <p className="text-xs text-muted-foreground">Desenvolvimento Web & Automação com IA</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-[18px] border border-border bg-card shadow-card">
          <div className="flex flex-wrap items-start justify-between gap-3.5 border-b border-border/60 px-7 pb-[22px] pt-[26px]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.06em] text-muted-foreground">
                Proposta para assinatura
              </p>
              <h1 className="mt-2 font-display text-[22px] font-bold tracking-[-.03em] text-foreground">
                {budget?.title}
              </h1>
              <p className="mt-[7px] text-[13px] text-muted-foreground">
                {refCode}
                {sentAt && ` · enviada em ${sentAt}`}
                {validUntil && ` · válida até ${validUntil}`}
              </p>
            </div>
            {done ? (
              <span
                className={`inline-flex h-[25px] items-center whitespace-nowrap rounded-lg px-[11px] text-[11.5px] font-bold ${
                  sig.status === "signed" ? "bg-ok-soft text-ok" : "bg-bad-soft text-bad"
                }`}
              >
                {sig.status === "signed" ? "Assinada" : "Rejeitada"}
              </span>
            ) : (
              <span className="inline-flex h-[25px] items-center whitespace-nowrap rounded-lg bg-accent px-[11px] text-[11.5px] font-bold text-primary">
                Aguardando você
              </span>
            )}
          </div>

          {docMode === "richtext" && budget?.richtext_content ? (
            <div className="border-b border-border/60 px-7 py-5">
              <div className="prose prose-sm max-h-[520px] max-w-none overflow-y-auto text-[13.5px] leading-[1.7] text-foreground [&_h1]:text-[18px] [&_h2]:text-[15px] [&_h3]:text-[13.5px] [&_table]:text-[12.5px] [&_th]:text-left">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{budget.richtext_content}</ReactMarkdown>
              </div>
            </div>
          ) : docMode === "pdf_upload" && budget?.base_pdf_url ? (
            <div className="border-b border-border/60">
              <iframe
                src={budget.base_pdf_url}
                className="h-[560px] w-full bg-muted"
                title="Proposta para assinatura"
              />
            </div>
          ) : (
            <>
              {items.length === 0 ? (
                <div className="flex items-center gap-4 border-b border-border/60 px-7 py-[15px] text-[13.5px]">
                  <div className="min-w-0 flex-1 font-semibold text-foreground">
                    {budget?.service_type?.name || budget?.title || "Serviço"}
                  </div>
                  <div className="w-[70px] text-muted-foreground">1</div>
                  <div className="w-[110px] text-right font-bold text-foreground">
                    {budget && fmtCurrency(budget.gross_total)}
                  </div>
                </div>
              ) : (
                items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-center gap-4 border-b border-border/60 px-7 py-[13px] text-[13.5px]"
                    data-testid="proposal-item"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground">{it.title}</div>
                      {it.pricing_mode === "hourly" && (
                        <div className="text-[11.5px] text-muted-foreground">
                          {it.hours ?? 0}h × {fmtCurrency(it.hourly_rate ?? 0)}/h
                        </div>
                      )}
                      {it.pricing_mode === "recurring" && (
                        <div className="text-[11.5px] text-muted-foreground">Cobrança mensal</div>
                      )}
                    </div>
                    <div className="w-[70px] text-muted-foreground">
                      {it.pricing_mode === "hourly" ? `${it.hours ?? 0} h` : "1"}
                    </div>
                    <div className="w-[110px] text-right font-bold text-foreground">
                      {fmtCurrency(it.subtotal)}
                      {it.pricing_mode === "recurring" ? "/mês" : ""}
                    </div>
                  </div>
                ))
              )}
              {items.length > 1 && (
                <div className="flex items-center gap-4 border-b border-border/60 px-7 py-[11px] text-[12.5px] text-muted-foreground">
                  <div className="min-w-0 flex-1">Subtotal dos serviços</div>
                  <div className="w-[110px] text-right font-semibold text-foreground">
                    {fmtCurrency(itemsGross)}
                  </div>
                </div>
              )}
              {discount > 0 && (
                <div
                  className="flex items-center gap-4 border-b border-border/60 px-7 py-[11px] text-[12.5px]"
                  data-testid="proposal-discount"
                >
                  <div className="min-w-0 flex-1 text-muted-foreground">
                    Desconto do pacote ({budget?.discount_percent ?? ""}%)
                  </div>
                  <div className="w-[110px] text-right font-semibold text-ok">
                    – {fmtCurrency(discount)}
                  </div>
                </div>
              )}
              {fee > 0 && (
                <div className="flex items-center gap-4 border-b border-border/60 px-7 py-[11px] text-[12.5px]">
                  <div className="min-w-0 flex-1 text-muted-foreground">Taxa da plataforma</div>
                  <div className="w-[110px] text-right font-semibold text-bad">
                    – {fmtCurrency(fee)}
                  </div>
                </div>
              )}
              {barter.barterTotal > 0 && (
                <div
                  className="border-b border-border/60 px-7 py-[11px] text-[12.5px]"
                  data-testid="proposal-barter"
                >
                  <div className="flex items-center gap-4">
                    <div className="min-w-0 flex-1 text-muted-foreground">
                      Permuta ({barter.barterPercent}%) — liquidado em serviços
                    </div>
                    <div className="w-[110px] text-right font-semibold text-foreground">
                      – {fmtCurrency(barter.barterTotal)}
                    </div>
                  </div>
                  {budget?.barter_description && (
                    <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted-foreground">
                      <span className="font-semibold text-foreground">Contrapartida:</span>{" "}
                      {budget.barter_description}
                    </p>
                  )}
                </div>
              )}
              {budget?.description && (
                <div className="border-b border-border/60 px-7 py-4">
                  <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-muted-foreground">
                    {budget.description}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4 bg-muted px-7 py-5">
            <Button
              variant="outline"
              size="sm"
              className="h-9 rounded-[10px] border-border bg-card text-[12.5px] font-semibold text-muted-foreground"
              onClick={() => {
                if (!budget) return;
                const pdf = resolveProposalPdf(budget);
                if (pdf.url) window.open(pdf.url, "_blank");
                else if (pdf.kind === "generated") generateBudgetPdf(budget);
              }}
            >
              <Download className="mr-2 h-3.5 w-3.5" /> Baixar proposta em PDF
            </Button>
            <div className="flex flex-wrap items-end justify-end gap-x-7 gap-y-3 text-right">
              {planRows.map((row) => (
                <div key={row.label}>
                  <div className="text-[11.5px] text-muted-foreground">{row.label}</div>
                  <div className="text-[15px] font-bold text-foreground">
                    {fmtCurrency(row.amount)}
                  </div>
                </div>
              ))}
              <div>
                <div className="text-[11.5px] text-muted-foreground">Total</div>
                <div className="font-display text-[22px] font-bold tracking-[-.03em] text-foreground">
                  {budget && fmtCurrency(budget.net_total)}
                </div>
              </div>
              {barter.barterTotal > 0 && (
                <div data-testid="proposal-cash">
                  <div className="text-[11.5px] text-muted-foreground">A cobrar</div>
                  <div className="font-display text-[22px] font-bold tracking-[-.03em] text-primary">
                    {fmtCurrency(barter.cashTotal)}
                  </div>
                  {barter.isFull && (
                    <div className="text-[11px] text-muted-foreground">
                      permuta integral — sem cobrança
                    </div>
                  )}
                </div>
              )}
              {recurringMonthly > 0 && (
                <div data-testid="proposal-recurring">
                  <div className="text-[11.5px] text-muted-foreground">Mensalidade</div>
                  <div className="font-display text-[22px] font-bold tracking-[-.03em] text-foreground">
                    {recurringInBarter ? "" : "+ "}
                    {fmtCurrency(recurringMonthly)}/mês
                  </div>
                  {recurringInBarter && (
                    <div className="text-[11.5px] text-muted-foreground">
                      incluída na permuta · sem cobrança
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {budget?.base_pdf_url && docMode !== "pdf_upload" && (
            <div className="border-t border-border/60">
              <button
                type="button"
                className="flex w-full items-center justify-between px-7 py-3 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                onClick={() => setShowPdf((v) => !v)}
              >
                Ver PDF
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showPdf ? "rotate-180" : ""}`}
                />
              </button>
              {showPdf && (
                <iframe
                  src={budget.base_pdf_url}
                  className="h-[520px] w-full bg-muted"
                  title="Proposta para assinatura"
                />
              )}
            </div>
          )}
        </div>

        {!done && canSign && (
          <div className="rounded-[18px] border border-border bg-card px-7 py-[26px] shadow-card">
            <h2 className="mb-1.5 text-base font-bold text-foreground">Assine para aprovar</h2>
            <p className="mb-[18px] text-[13px] text-muted-foreground">
              Assinando como <strong className="text-foreground">{sig.signer_name}</strong> ·{" "}
              {sig.signer_email}
            </p>

            <SignatureTabs
              tab={tab}
              onTabChange={setTab}
              signerName={sig.signer_name}
              typedName={typedName}
              onTypedNameChange={setTypedName}
              certFile={certFile}
              onCertFileChange={setCertFile}
              certPwd={certPwd}
              onCertPwdChange={setCertPwd}
              sigPadRef={sigPadRef}
              canvasHeight={170}
            />

            <div className="mt-5 rounded-xl border border-border bg-muted px-4 py-3.5 text-[11.5px] leading-[1.6] text-muted-foreground">
              Ao assinar, registramos data, hora e o IP de onde a assinatura partiu. Você recebe uma
              cópia em PDF por e-mail.
              <label
                htmlFor="lgpd-consent"
                className="mt-2.5 flex cursor-pointer items-start gap-2.5"
              >
                <Checkbox
                  id="lgpd-consent"
                  checked={lgpdConsent}
                  onCheckedChange={(v) => setLgpdConsent(!!v)}
                  className="mt-0.5"
                />
                <span>
                  Concordo com o tratamento da minha assinatura eletrônica conforme a Lei nº
                  14.063/2020 e a LGPD (Lei nº 13.709/2018).
                </span>
              </label>
            </div>

            <div className="mt-[18px] flex flex-wrap gap-2.5">
              <Button
                onClick={handleSign}
                disabled={submitting || !lgpdConsent}
                className="h-[50px] min-w-[200px] flex-1 rounded-[13px] text-[15px] font-bold"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                Assinar e aprovar proposta
              </Button>
              <Button
                variant="outline"
                className="h-[50px] rounded-[13px] border-border bg-card px-5 text-[13.5px] font-semibold text-muted-foreground"
                onClick={() => setShowReject((v) => !v)}
                disabled={submitting}
              >
                Recusar
              </Button>
            </div>

            {showReject && (
              <RejectInline
                reason={rejectReason}
                onReasonChange={setRejectReason}
                onSubmit={handleReject}
                submitting={submitting}
                placeholder="Explique o que precisa mudar para eu ajustar a proposta."
              />
            )}
          </div>
        )}

        {!done && !canSign && (
          <div className="rounded-[18px] border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-card">
            Aguardando assinatura de signatários anteriores. Você receberá um novo aviso quando for
            sua vez.
          </div>
        )}

        {done && (
          <div
            className={`rounded-[18px] border p-8 text-center shadow-card ${sig.status === "signed" ? "border-ok bg-ok-soft" : "border-border bg-card"}`}
          >
            {sig.status === "signed" ? (
              <>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ok text-ok-foreground">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <p className="mt-3 font-medium text-foreground">
                  Sua assinatura foi registrada. Obrigado!
                </p>
              </>
            ) : (
              <>
                <XCircle className="mx-auto mb-2 h-12 w-12 text-destructive" />
                <p className="font-medium text-foreground">Proposta rejeitada.</p>
              </>
            )}
          </div>
        )}

        <SignersCard title="Quem já assinou" signers={signers} currentEmail={sig.signer_email} />
      </div>
    </div>
  );
}

// Página por token: nunca indexar nem gerar preview com dado do cliente
// (docs/specs/standards/pages-and-routing.md, regra 2).
export default function SignBudget() {
  return (
    <>
      <SEOMeta
        title="Assinatura de proposta"
        description="Página privada de assinatura de proposta."
        noindex
      />
      <SignBudgetView />
    </>
  );
}
